import * as React from "react";

export function Avatar({
  who,
  size = 24,
  dark = false,
  fontSize,
  name,
}: {
  who: string;
  size?: number;
  dark?: boolean;
  fontSize?: number;
  name?: string;
}) {
  return (
    <div
      aria-label={name || who}
      title={name}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: dark ? "var(--logo-bg)" : "var(--card-2)",
        color: dark ? "var(--logo-fg)" : "var(--meta)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: fontSize ?? size * 0.4,
        fontWeight: 600,
        flex: "0 0 auto",
      }}
    >
      {who}
    </div>
  );
}

/** Derive 1–2 initials from a display name or email. */
export function initialsFromUser(name?: string, email?: string): string {
  const source = (name || email || "?").trim();
  if (!source) return "?";
  if (source.includes("@")) {
    const local = source.split("@")[0] || "?";
    const parts = local.split(/[._\-\s]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
  }
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function CennaLogo({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={(size * 134) / 140}
      viewBox="0 0 140 134"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M140 65.9999V17.7806C140 7.96065 132.039 0 122.22 0H68C30.4446 0 0 30.4446 0 67.9999C0 105.555 30.4447 134 68.0001 134H70C108.66 134 140 104.66 140 65.9999Z"
        fill="var(--logo-bg, #27272a)"
      />
      <path
        d="M28.1332 92.2142C24.2518 95.7871 24 101.5 27.4998 105.5C32.1084 109.981 39.4625 110.216 44.1939 105.857L99.2997 54.2788V90.9847C99.2997 96.5429 103.933 101.048 109.65 101.048C115.366 101.048 120 96.5429 120 90.9847V30.1266C120 24.5688 115.366 20.0632 109.65 20.0632L46.3498 20C40.6336 20 35.9998 24.5055 35.9998 30.0634C35.9998 35.6213 40.6336 40.1268 46.3498 40.1268L83.7742 40.1901L28.1332 92.2142Z"
        fill="var(--logo-fg, #ffffff)"
      />
    </svg>
  );
}

type IconProps = {
  size?: number;
  stroke?: number;
  className?: string;
  style?: React.CSSProperties;
};

function stroke(paths: string[]) {
  return function Icon({ size = 17, stroke: sw = 1.8, className, style }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={style}
        aria-hidden
      >
        {paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  };
}

export const IconSun = stroke([
  "M12 3v2",
  "M12 19v2",
  "M5.2 5.2l1.4 1.4",
  "M17.4 17.4l1.4 1.4",
  "M3 12h2",
  "M19 12h2",
  "M5.2 18.8l1.4-1.4",
  "M17.4 6.6l1.4-1.4",
  "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
]);
export const IconMoon = stroke(["M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"]);
export const IconMonitor = stroke([
  "M8 21h8",
  "M12 17v4",
  "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
]);
export const IconLogOut = stroke([
  "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4",
  "M16 17l5-5-5-5",
  "M21 12H9",
]);
export const IconExternalLink = stroke([
  "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
  "M15 3h6v6",
  "M10 14L21 3",
]);
export const IconCheck = stroke(["M20 6L9 17l-5-5"]);
export const IconChevronDown = stroke(["M6 9l6 6 6-6"]);
